import React, { useState } from 'react';
import { Calendar, Clock, Check, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingSection = ({ username, profile, theme, themeVars }) => {
  const t = theme;
  const { isDark, subtleBg, subtleBorder, mutedText } = themeVars;
  const [showBooking, setShowBooking] = useState(false);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingSlots, setBookingSlots] = useState([]);
  const [bookingForm, setBookingForm] = useState({ time: '', name: '', email: '', phone: '', note: '' });
  const [bookingStep, setBookingStep] = useState('select');
  const [bookingLoading, setBookingLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchSlots = async (date) => {
    setBookingDate(date);
    setBookingSlots([]);
    setBookingForm(prev => ({ ...prev, time: '' }));
    try {
      const res = await axios.get(`${API}/p/${username}/availability?date=${date}`);
      setBookingSlots(res.data.slots || []);
    } catch {}
  };

  const submitBooking = async () => {
    setBookingLoading(true);
    try {
      await axios.post(`${API}/p/${username}/book`, { date: bookingDate, ...bookingForm });
      setBookingStep('done');
    } catch (err) { setError(err.response?.data?.detail || 'Booking failed'); }
    finally { setBookingLoading(false); }
  };

  if (!profile.availability) return null;

  return (
    <div className="mt-8" data-testid="public-booking-section">
      {!showBooking ? (
        <button onClick={() => setShowBooking(true)}
          className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all hover:scale-[1.02] active:scale-[0.98]"
          style={{ background: subtleBg, border: `1px solid ${subtleBorder}`, color: t.text_color }}
          data-testid="book-appointment-btn">
          <Calendar className="w-4 h-4" style={{ color: t.primary_color }} /> Book an Appointment
        </button>
      ) : bookingStep === 'done' ? (
        <div className="p-6 rounded-xl text-center" style={{ background: subtleBg, border: `1px solid ${subtleBorder}` }}>
          <Check className="w-8 h-8 mx-auto mb-2" style={{ color: t.primary_color }} />
          <p className="text-sm font-semibold mb-1" style={{ color: t.text_color }}>Booked!</p>
          <p className="text-xs" style={{ color: mutedText }}>{bookingDate} at {bookingForm.time}</p>
          {profile.availability.booking_message && <p className="text-xs mt-2" style={{ color: mutedText }}>{profile.availability.booking_message}</p>}
        </div>
      ) : (
        <div className="p-4 rounded-xl" style={{ background: subtleBg, border: `1px solid ${subtleBorder}` }}>
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: mutedText }}>Book a Time</p>
            <button onClick={() => { setShowBooking(false); setBookingStep('select'); }} style={{ color: mutedText }}><X className="w-4 h-4" /></button>
          </div>
          <input type="date" value={bookingDate} onChange={e => fetchSlots(e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full bg-white/10 border rounded-lg px-3 py-2.5 text-sm mb-3 outline-none"
            style={{ borderColor: subtleBorder, color: t.text_color, colorScheme: isDark ? 'dark' : 'light' }}
            data-testid="booking-date-picker" />
          {bookingDate && bookingSlots.length === 0 && (
            <p className="text-xs text-center py-3" style={{ color: mutedText }}>No available slots for this date</p>
          )}
          {bookingSlots.length > 0 && bookingStep === 'select' && (
            <div className="grid grid-cols-3 gap-1.5 mb-3" data-testid="booking-time-grid">
              {bookingSlots.map(time => (
                <button key={time} onClick={() => { setBookingForm(prev => ({ ...prev, time })); setBookingStep('form'); }}
                  className="py-2 rounded-lg text-xs font-semibold transition-all hover:scale-105"
                  style={{ background: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.04)', color: t.text_color }}
                  data-testid={`booking-slot-${time}`}>
                  {time}
                </button>
              ))}
            </div>
          )}
          {bookingStep === 'form' && (
            <div className="space-y-2" data-testid="booking-form">
              <p className="text-xs font-semibold mb-2" style={{ color: t.text_color }}>
                <Clock className="w-3 h-3 inline mr-1" />{bookingDate} at {bookingForm.time}
                <button onClick={() => setBookingStep('select')} className="ml-2 underline text-[10px]" style={{ color: mutedText }}>change</button>
              </p>
              <input value={bookingForm.name} onChange={e => setBookingForm(prev => ({ ...prev, name: e.target.value }))} placeholder="Your name *"
                className="w-full bg-white/10 border rounded-lg px-3 py-2 text-sm outline-none" style={{ borderColor: subtleBorder, color: t.text_color }} />
              <input value={bookingForm.email} onChange={e => setBookingForm(prev => ({ ...prev, email: e.target.value }))} placeholder="Email"
                className="w-full bg-white/10 border rounded-lg px-3 py-2 text-sm outline-none" style={{ borderColor: subtleBorder, color: t.text_color }} />
              <input value={bookingForm.phone} onChange={e => setBookingForm(prev => ({ ...prev, phone: e.target.value }))} placeholder="Phone"
                className="w-full bg-white/10 border rounded-lg px-3 py-2 text-sm outline-none" style={{ borderColor: subtleBorder, color: t.text_color }} />
              <input value={bookingForm.note} onChange={e => setBookingForm(prev => ({ ...prev, note: e.target.value }))} placeholder="Note (optional)"
                className="w-full bg-white/10 border rounded-lg px-3 py-2 text-sm outline-none" style={{ borderColor: subtleBorder, color: t.text_color }} />
              {error && <p className="text-xs text-red-400">{error}</p>}
              <button onClick={submitBooking} disabled={!bookingForm.name || bookingLoading}
                className="w-full py-2.5 rounded-lg font-semibold text-sm transition-all hover:scale-[1.02] disabled:opacity-50"
                style={{ background: t.primary_color, color: t.bg_color }}
                data-testid="confirm-booking-btn">
                {bookingLoading ? 'Booking...' : 'Confirm Booking'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BookingSection;
